import React, { useState, useEffect } from "react";
import { Input, Button, Table, Modal, Form, Select, message, Space } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";

const { TextArea } = Input;
const { Option } = Select;

interface User {
  key: number;
  _id?: string;
  pageTitle: string;
  pageDescription: string;
  pageName: string;
  canonical: string;
  userPostDataInfo?: Array<{
    name: string;
    role: string;
    emailId: string;
    createdAt: string;
    updatedAt: string;
  }>;
}

const Keyword = () => {
  const [form] = Form.useForm();
  const [MetaData, setMetaData] = useState<User[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editUserIndex, setEditUserIndex] = useState<number | string | null>(null);

  const getUserRole = (): string => {
    const userDetailsString = localStorage.getItem('userDetails');
    const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
    return userDetails?.role || '';
  };
  const userRole = getUserRole();
  const isAdmin = userRole === 'admin';

  const fetchData = async () => {
    try {
      const userDetailsString = localStorage.getItem('userDetails');
      const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
      const role = userDetails?.role;
      const userId = userDetails?.userId;

      let url = 'https://service.ireedindia.com/v1/find-page?size=25';
      if (role !== 'admin' && userId) {
        url += `&userId=${encodeURIComponent(userId)}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch data");
      const responseData = await response.json();
      setMetaData(responseData.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onFinish = async (values: any) => {
    try {
      const userDetailsString = localStorage.getItem('userDetails');
      const userDetails = userDetailsString ? JSON.parse(userDetailsString) : null;
      const userId = userDetails?.userId;

      if (editUserIndex !== null) {
        const response = await axios.patch(
          `https://service.ireedindia.com/v1/edit-seo/${editUserIndex}`,
          values
        );
        if (response.status !== 200) throw new Error("Failed to update");
        message.success("Record updated successfully");
        fetchData();
      } else {
        const recordPayload = { ...values, userId: userId };
        await handleAdd(recordPayload);
      }
      form.resetFields();
      setIsModalVisible(false);
      setEditUserIndex(null);
    } catch (error) {
      console.error("Error saving record:", error);
    }
  };

  const handleAdd = async (values: any) => {
    try {
      const response = await axios.post(`https://service.ireedindia.com/v1/create-page`, values);
      if (response.status === 201) {
        fetchData();
        message.success("Record added successfully");
      }
    } catch (error) {
      message.error("Failed to add record");
    }
  };

  const handleEdit = (record: User) => {
    form.setFieldsValue(record);
    setIsModalVisible(true);
    setEditUserIndex(record._id || record.key || null);
  };

  const handleDelete = async (record: User) => {
    if (!isAdmin) {
      message.error('You do not have permission to delete records');
      return;
    }
    try {
      const deleteKey = record._id || record.key;
      const response = await axios.delete(`https://service.ireedindia.com/v1/delete-seo/${deleteKey}`);
      if (response.status === 200) {
        message.success("Record deleted successfully");
        setMetaData(MetaData.filter((item) => (item._id !== deleteKey && item.key !== deleteKey)));
      }
    } catch (error) {
      console.error("Error deleting record:", error);
    }
  };

  const successColumns = [
    { title: "Page Title", dataIndex: "pageTitle", key: "pageTitle" },
    { title: "Page Description", dataIndex: "pageDescription", key: "pageDescription" },
    { title: "Page Name", dataIndex: "pageName", key: "pageName" },
    {
      title: "Canonical URL",
      dataIndex: "canonical",
      key: "canonical",
      width: 150,
      render: (url: string) => url ? <a href={url} target="_blank" rel="noreferrer">Link</a> : 'N/A',
    },
    ...(isAdmin ? [
      {
        title: "Created By",
        dataIndex: "userPostDataInfo",
        key: "createdBy",
        width: 180,
        render: (info: any) => {
          if (!info || info.length === 0) return 'N/A';
          const user = info[0];
          return (
            <div style={{ fontSize: '12px' }}>
              <div style={{ fontWeight: '600' }}>{user.name}</div>
              <div style={{ color: '#6b7280' }}>{user.emailId}</div>
            </div>
          );
        },
      },
    ] : []),
    {
      title: "Action",
      key: "action",
      render: (_: any, record: User) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          {isAdmin && (
            <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record)} />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Meta Data</h2>
        <Button type="primary" onClick={() => setIsModalVisible(true)}>Add Data</Button>
      </div>
      
      <Table columns={successColumns} dataSource={MetaData} rowKey="_id" />

      <Modal
        title={editUserIndex !== null ? "Edit Data" : "Add Data"}
        visible={isModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
          setEditUserIndex(null);
        }}
        footer={null}
      >
        <Form form={form} name="metadata" onFinish={onFinish} labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
          <Form.Item label="Page Name" name="pageName" rules={[{ required: true }]}>
           <Select>
              <Option value="guaranteed-placement-program">guaranteed-placement-program</Option>
              <Option value="mba-in-business-analytics">mba-in-business-analytics</Option>
              <Option value="real-estate-advance-program">real-estate-advance-program</Option>
              <Option value="real-estate-advanced-corporate-training">real-estate-advanced-corporate-training</Option>
              <Option value="real-estate-digital-marketing-intelligence">real-estate-digital-marketing-intelligence</Option>
              <Option value="real-estate-management-advancement-program">real-estate-management-advancement-program</Option>
              <Option value="soft-skill-and-personality-development">soft-skill-and-personality-development</Option>
              <Option value="Home-Page">Home-Page</Option>
              <Option value="About-Us">About-Us</Option>
              <Option value="Contact-Us">Contact-Us</Option>
              <Option value="FAQ">FAQ</Option>
              <Option value="Success Story">Success Story </Option>
              <Option value="Blog">Blog</Option>
              <Option value="Terms and Conditions">Terms and Conditions</Option>
              <Option value="Gallery">Gallery</Option>
              <Option value="Mentors">Mentors</Option>
              <Option value="News">News</Option>
              <Option value="Privacy-Policy">Privacy-Policy</Option>
              <Option value="job">job</Option>
              <Option value="blog">blog</Option>
              <Option value="post-graduation-diploma-in-digital-marketing-intelligence">post-graduation-diploma-in-digital-marketing-intelligence</Option>

            </Select>
          </Form.Item>
          <Form.Item label="Page Title" name="pageTitle" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Page Description" name="pageDescription">
            <TextArea />
          </Form.Item>
          <Form.Item label="Canonical URL" name="canonical">
            <Input placeholder="https://example.com/course-name" />
          </Form.Item>
          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button type="primary" htmlType="submit">
              {editUserIndex !== null ? "Update" : "Add"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Keyword;